/**
 * `workflowPresenceCache.ts` — what the projects grid shows for a project's
 * workflow, answered once per project per session.
 *
 * Feeds the marker on the projects grid (`ProjectItemView`): whether there IS a
 * workflow, and — when the stored file parses — how many steps a learner is
 * taken through and roughly how long it takes. Shaped after
 * `models/storage/thumbnailCache.ts`: module-scoped, keyed
 * `customerId|projectId`, with in-flight de-duplication so a grid that renders
 * the same card twice (grid → list layout switch) issues one request.
 *
 * The S3 OBJECT is the oracle, deliberately — not a project-row flag and not
 * the Dynamo summary:
 *  - A project-row attribute would have to be written by whichever client
 *    authored the workflow, and the desktop client (`Models/WorkflowModel/*`)
 *    writes no such flag, so a Unity-authored workflow would show as absent.
 *  - The `{customerId}:workflow` summary row carries NO project id — the web
 *    DTO mirrors Unity's `WorkflowSummary` field-for-field — so it cannot be
 *    joined back to a project. Adding one would be a divergence in a row Unity
 *    also writes as a whole-item PutItem, i.e. the next desktop save would drop
 *    it again and silently un-badge the project. Its `steps` list is also the
 *    whole DOCUMENT, parked steps included, so it would answer the count wrong
 *    even if it could be joined.
 *
 * The probe is the existing `getWorkflowYaml` GET rather than a `HeadObject`:
 * that GET is the request the app already makes on every project open, so it
 * needs no new S3 CORS method (the bucket is configured for GET). A workflow
 * file is a few KB, which is an order of magnitude under the thumbnail the same
 * card already fetches — and because the whole file is already in hand, the
 * step count and duration cost nothing beyond parsing it.
 *
 * Parsing goes through the canonical `parseWorkflowDocumentText`, never a
 * second shallower reader: a private "just find `steps:`" scan is a second
 * reader of a format whose whole change checklist exists to stop surfaces
 * drifting from the codec.
 *
 * A probe that FAILS is not cached — `getWorkflowYaml` throws on anything that
 * is not a genuine 404/403, and caching a transient failure as "no workflow"
 * would leave the marker missing for the rest of the session.
 */

import { Ioc } from "@core/ioc/ioc";
import { STORAGE_MODEL, type IStorageModel } from "@models/storage/IStorageModel";
import { getPlayableSteps, type WorkflowDocument } from "./WorkflowDocument";
import { parseWorkflowDocumentText } from "./WorkflowYaml";

/**
 * The two facts a project card states about a workflow, plus whether there is
 * one at all.
 *
 * NOT to be confused with `WorkflowSummaryDto` (`WorkflowSummary.ts`), which is
 * the searchable DynamoDB projection Unity also writes. This one never leaves
 * the browser.
 */
export interface WorkflowPresence {
  /** Whether the project has a stored `workflow.yaml`. */
  readonly present: boolean;
  /**
   * PLAYABLE steps — what a learner is actually taken through, so parked
   * (`disabled: true`) steps are excluded, exactly as the run scopes them. A
   * card promising twelve steps for a run that shows ten is the same lie the
   * workflow rule keeps out of the position counter.
   *
   * `null` means "unknown", never "none": the file is there and does not
   * parse, so there is no honest number to print.
   */
  readonly stepCount: number | null;
  /**
   * The header's `takes_about`, VERBATIM — free author text ("5 min", "about
   * an hour"), so it is printed rather than reformatted. `null` when the
   * author did not provide one, which is what leaves it off the card.
   */
  readonly duration: string | null;
}

/** No stored file. */
export const K_WORKFLOW_ABSENT: WorkflowPresence = {
  present: false,
  stepCount: null,
  duration: null,
};

/** A stored file that does not parse: it exists, and nothing about it is known. */
export const K_WORKFLOW_UNREADABLE: WorkflowPresence = {
  present: true,
  stepCount: null,
  duration: null,
};

/** What a card states about a loaded document. */
export function presenceOfDocument(document: WorkflowDocument): WorkflowPresence {
  const duration = document.expectedDuration?.trim() ?? "";
  return {
    present: true,
    stepCount: getPlayableSteps(document).length,
    duration: duration.length > 0 ? duration : null,
  };
}

/**
 * The three states a stored file can be in, in ONE place: absent, present but
 * unreadable, present and parsed. Both the grid's probe and
 * `ProjectWorkflowModel`'s load path map through this, so they cannot come to
 * different answers about the same bytes.
 */
export function presenceOfStoredFile(
  rawText: string | null,
  document: WorkflowDocument | null,
): WorkflowPresence {
  if (document !== null) return presenceOfDocument(document);
  if (rawText !== null && rawText.trim().length > 0) return K_WORKFLOW_UNREADABLE;
  return K_WORKFLOW_ABSENT;
}

const s_presence = new Map<string, WorkflowPresence>();
const s_inFlight = new Map<string, Promise<WorkflowPresence>>();

function keyOf(customerId: string, projectId: string): string {
  return `${customerId}|${projectId}`;
}

/** Cached answer without probing; `undefined` when this project is unknown. */
export function peekWorkflowPresence(
  customerId: string,
  projectId: string,
): WorkflowPresence | undefined {
  return s_presence.get(keyOf(customerId, projectId));
}

/**
 * What the project's stored workflow is. Cached for the tab's life and kept
 * current by {@link setWorkflowPresence}, which `ProjectWorkflowModel` calls
 * whenever it writes or deletes one.
 */
export async function readWorkflowPresence(
  customerId: string,
  projectId: string,
  signal?: AbortSignal,
): Promise<WorkflowPresence> {
  if (!customerId || !projectId) return K_WORKFLOW_ABSENT;
  const key = keyOf(customerId, projectId);

  const cached = s_presence.get(key);
  if (cached !== undefined) return cached;

  const pending = s_inFlight.get(key);
  if (pending) return await pending;

  const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
  const task = (async () => {
    const text = await storage.getWorkflowYaml(customerId, projectId, signal);
    // Bytes that do not parse still COUNT as present: the project HAS a
    // workflow, it is just broken, and the card answers "is there one" rather
    // than "is it playable".
    const document =
      text !== null && text.trim().length > 0
        ? parseWorkflowDocumentText(text).document
        : null;
    const presence = presenceOfStoredFile(text, document);
    s_presence.set(key, presence);
    return presence;
  })();

  s_inFlight.set(key, task);
  try {
    return await task;
  } finally {
    s_inFlight.delete(key);
  }
}

/**
 * Records what the app just wrote, so returning to the grid after creating,
 * editing or deleting a workflow shows the right marker without a re-probe.
 */
export function setWorkflowPresence(
  customerId: string,
  projectId: string,
  presence: WorkflowPresence,
): void {
  if (!customerId || !projectId) return;
  s_presence.set(keyOf(customerId, projectId), presence);
}

/** Drops every cached answer (sign-out, "Clear Cache"). */
export function clearWorkflowPresenceCache(): void {
  s_presence.clear();
}
